import { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Tab,
  Tabs,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import { STORAGE_URL } from "../../../variables";
import ConfirmacaoAproveitamentoModal from "./ConfirmacaoAproveitamentoModal";

// --- CONSTANTS & HELPERS ---
const EXAM_TYPES = {
  SUPLETIVO_ANUAL_ESPECIAL_FUNDAMENTAL: "supletivo_anual_especial_fundamental",
  SUPLETIVO_ANUAL_ESPECIAL_MEDIO: "supletivo_anual_especial_medio",
  ENCCEJA_FUNDAMENTAL: "encceja_fundamental",
  ENCCEJA_MEDIO: "encceja_medio",
  ENEM: "enem",
};

const EXAM_LABELS = {
  [EXAM_TYPES.ENEM]: "ENEM",
  [EXAM_TYPES.ENCCEJA_FUNDAMENTAL]: "ENCCEJA Fundamental",
  [EXAM_TYPES.ENCCEJA_MEDIO]: "ENCCEJA Médio",
  [EXAM_TYPES.SUPLETIVO_ANUAL_ESPECIAL_FUNDAMENTAL]: "Supletivo Fundamental",
  [EXAM_TYPES.SUPLETIVO_ANUAL_ESPECIAL_MEDIO]: "Supletivo Médio",
};

const EXAM_AREAS = {
  [EXAM_TYPES.ENEM]: {
    "Linguagens e suas Tecnologias":'disciplina_linguagens',
    "Ciências Humanas e suas Tecnologias":'disciplina_humanas',
    "Ciências da Natureza e suas Tecnologias":'disciplina_natureza',
    "Matemática e suas Tecnologias":'disciplina_matematica_area',
    "Redação":'redacao',
  },
  [EXAM_TYPES.ENCCEJA_FUNDAMENTAL]: {
    "Ciências Naturais (Ciências)":'disciplina_ciencias',
    "Matemática (Matemática)":'disciplina_matematica',
    "Linguagens (Arte, Língua Portuguesa e Língua Estrangeira Morderna)":'disciplina_linguagens',
    "Ciências Humanas (História e Geografia)":'disciplina_ciencias_humanas',
    "Redação":'redacao'
  },
  [EXAM_TYPES.ENCCEJA_MEDIO]: {
    "Ciências da Natureza e suas Tecnologias (Química, Física e Biologia)":'disciplina_natureza',
    "Matemática e suas Tecnologias (Matemática)":'disciplina_matematica_area',
    "Linguagens e suas Tecnologias (Língua Portuguesa, Língua Estrangeira Moderna, Artes e Educação Física)":'disciplina_linguagens',
    "Ciências Humanas e suas Tecnologias (História, Geografia, Filosofia e Sociologia)":'disciplina_humanas',
    "Redação":'redacao'
  },
  [EXAM_TYPES.SUPLETIVO_ANUAL_ESPECIAL_FUNDAMENTAL]: {
    "Ciências Naturais (Ciências)":'disciplina_ciencias',
    "Matemática (Matemática)":'disciplina_matematica',
    "Linguagens (Arte, Língua Portuguesa e Língua Estrangeira Morderna)":'disciplina_linguagens',
    "Ciências Humanas (História e Geografia)":'disciplina_ciencias_humanas',
    "Redação":'redacao'
  },
  [EXAM_TYPES.SUPLETIVO_ANUAL_ESPECIAL_MEDIO]: {
    "Linguagens e suas Tecnologias (Língua Portuguesa, Língua Estrangeira Moderna, Artes e Educação Física)":'disciplina_linguagens',
    "Ciências Humanas e suas Tecnologias (História, Geografia, Filosofia e Sociologia)":'disciplina_humanas',
    "Ciências da Natureza e suas Tecnologias (Química, Física e Biologia)":'disciplina_natureza',
    "Matemática (Matemática)":'disciplina_matematica_area',
    "Redação":'redacao'
  },
};

const getDisciplines = (area, examType = null) => {
  // if (
  //   examType === EXAM_TYPES.SUPLETIVO_ANUAL_ESPECIAL_MEDIO &&
  //   area === "Linguagens, Códigos e suas Tecnologias"
  // ) {
  //   return [
  //     "Linguagens, Códigos e suas Tecnologias",
  //     "Língua Estrangeira Moderna (Inglês ou Espanhol)",
  //   ];
  // }
  return [area];
};

// --- SUB-COMPONENTS ---
const DocumentPreview = ({ docUrl }) => (
  <Col md={7}>
    <h5 className="mb-3 text-dark border-bottom pb-2">
      Documento Comprobatório
    </h5>
    {docUrl ? (
      <div
        style={{
          height: "650px",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          backgroundColor: "#f8f9fa",
        }}
      >
        <iframe
          title="preview"
          src={docUrl}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
        <div className="mt-2 text-right">
          <a
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-info"
          >
            <i className="fa fa-external-link mr-1"></i> Abrir em nova guia
          </a>
        </div>
      </div>
    ) : (
      <div className="alert alert-warning">
        Documento não disponível para visualização.
      </div>
    )}
  </Col>
);

const ExamForm = ({ data, errors, onChange, onNotaChange, loading }) => {
  const areas = useMemo(() => EXAM_AREAS[data.tipo] || [], [data.tipo]);

  return (
    <Col md={5}>
      <h5 className="mb-3 text-dark border-bottom pb-2">Dados de Aprovação</h5>
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Ano</Form.Label>
              <Form.Control
                type="number"
                placeholder="2023"
                max={new Date().getFullYear()}
                min={2000}
                value={data.ano_realizacao || ""}
                onChange={(e) => onChange("ano_realizacao", e.target.value)}
                isInvalid={!!errors.ano_realizacao}
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.ano_realizacao}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Nº Inscrição</Form.Label>
              <Form.Control
                type="text"
                placeholder="Inscrição"
                value={data.numero_inscricao || ""}
                onChange={(e) => onChange("numero_inscricao", e.target.value)}
                isInvalid={!!errors.numero_inscricao}
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.numero_inscricao}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <hr />
        <h6 className="font-weight-bold mt-3 mb-3">
          Notas por Área e Disciplina
        </h6>

        {Object.keys(areas).map((area_index) => {
          let area=areas[area_index]
          const disciplines = getDisciplines(area, data.tipo);
          const isComposite = disciplines.length > 1;

          return (
            <div key={area} className="mb-4">
              {isComposite && (
                <h6
                  className="text-primary font-weight-bold mb-2"
                  style={{ fontSize: "0.95rem" }}
                >
                  {area}
                </h6>
              )}
              {disciplines.map((disc) => (
                <Form.Group key={disc} className="mb-2">
                  <Form.Label className="mb-1 small">{area_index}</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      max="1000"
                      placeholder="0.00"
                      value={data.notas?.[disc] || ""}
                      onChange={(e) => onNotaChange(disc, e.target.value)}
                      isInvalid={!!errors[`notas.${disc}`]}
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors[`notas.${disc}`]}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              ))}
            </div>
          );
        })}

        {Object.keys(areas).length === 0 && (
          <div className="alert alert-secondary">
            Nenhuma área mapeada para este exame.
          </div>
        )}
      </Form>
    </Col>
  );
};

// --- MAIN COMPONENT ---
const ExamesAnterioresModal = ({
  show,
  onHide,
  onConfirm,
  onReject,
  inscricao,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formsData, setFormsData] = useState({});
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (show && inscricao?.examesAnteriores) {
      const initialData = {};
      inscricao.examesAnteriores.forEach((exame, index) => {
        const type = exame.exame_nome || exame.nome;
        const areas = EXAM_AREAS[type] || [];
        const notas = {};

        Object.keys(areas).forEach((area_index) => {
          let area = areas[area_index]
          getDisciplines(area, type).forEach((disc) => (notas[disc] = ""));
        });

        initialData[index] = {
          ano_realizacao: exame.exame_anos || exame.anos || "",
          numero_inscricao: "",
          notas,
          tipo: type,
        };
      });
      setFormsData(initialData);
      setActiveTab(0);
      setErrors({});
      setShowConfirmModal(false);
    }
  }, [show, inscricao]);

  const updateField = (index, field, value) => {
    setFormsData((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: value },
    }));
    if (errors[`${index}.${field}`])
      setErrors((prev) => ({ ...prev, [`${index}.${field}`]: null }));
  };

  const updateNota = (index, disc, value) => {
    if (value < 0 || value > 1000) return;
    setFormsData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        notas: { ...prev[index].notas, [disc]: value },
      },
    }));
    if (errors[`${index}.notas.${disc}`])
      setErrors((prev) => ({ ...prev, [`${index}.notas.${disc}`]: null }));
  };

  const validate = () => {
    const currentData = formsData[activeTab];
    if (!currentData) return false;
    const newErrors = {};
    let isValid = true;
    const currentYear = new Date().getFullYear();

    if (!currentData.ano_realizacao) {
      newErrors[`${activeTab}.ano_realizacao`] = "Obrigatório";
      isValid = false;
    } else if (
      currentData.ano_realizacao < 2000 ||
      currentData.ano_realizacao > currentYear
    ) {
      newErrors[`${activeTab}.ano_realizacao`] = "Ano inválido";
      isValid = false;
    }

    if (!currentData.numero_inscricao) {
      newErrors[`${activeTab}.numero_inscricao`] = "Obrigatório";
      isValid = false;
    }

    const areas = EXAM_AREAS[currentData.tipo] || [];
    Object.keys(areas).forEach((area_index) => {
      let area = areas[area_index]
      getDisciplines(area, currentData.tipo).forEach((disc) => {
        if (!currentData.notas[disc]) {
          newErrors[`${activeTab}.notas.${disc}`] = "Obrigatório";
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleFinalConfirm = () => {
    let payload = [];
    Object.values(formsData).map((currentData) => {
      payload.push({
        tipo: currentData.tipo,
        ano_realizacao: parseInt(currentData.ano_realizacao, 10),
        numero_inscricao: currentData.numero_inscricao,
        notas: Object.entries(currentData.notas).map(([area, nota]) => {
          return {
            area: area,
            nota: parseFloat(nota),
          };
        }),
      });
    })

    onConfirm(payload);
    setShowConfirmModal(false);
  };

  const getDocUrl = (exame) => {
    const path =
      exame.exame_documento_caminho ||
      exame.documento_caminho ||
      exame.arquivoCaminho;
    return path ? `${path}` : null;
  };

  if (!inscricao?.examesAnteriores?.length) return null;

  const currentData = formsData[activeTab];
  const currentExamName = currentData
    ? EXAM_LABELS[currentData.tipo] || currentData.tipo
    : "Exame";
  const currentErrors = {
    ano_realizacao: errors[`${activeTab}.ano_realizacao`],
    numero_inscricao: errors[`${activeTab}.numero_inscricao`],
    ...Object.keys(errors).reduce((acc, key) => {
      if (key.startsWith(`${activeTab}.notas.`))
        acc[key.replace(`${activeTab}.`, "")] = errors[key];
      return acc;
    }, {}),
  };

  return (
    <>
      <Modal
        show={show}
        onHide={loading ? null : onHide}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Aprovação por Exames Anteriores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(parseInt(k, 10))}
            className="mb-3"
          >
            {inscricao.examesAnteriores.map((exame, index) => (
              <Tab
                eventKey={index}
                title={
                  EXAM_LABELS[exame.exame_nome || exame.nome] ||
                  `Exame ${index + 1}`
                }
                key={index}
              >
                <Row>
                  <DocumentPreview docUrl={getDocUrl(exame)} />
                  <ExamForm
                    data={formsData[index] || {}}
                    errors={currentErrors}
                    onChange={(field, val) => updateField(index, field, val)}
                    onNotaChange={(disc, val) => updateNota(index, disc, val)}
                    loading={loading}
                  />
                </Row>
              </Tab>
            ))}
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={onReject || onHide}
            disabled={loading}
            className="mr-auto"
          >
            Rejeitar Aproveitamento
          </Button>
          <div>
            <Button
              variant="outline-secondary"
              onClick={onHide}
              disabled={loading}
              className="mr-2"
            >
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={() => validate() && setShowConfirmModal(true)}
              disabled={loading}
            >
              {loading ? "Salvando..." : "Confirmar e Aprovar"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmacaoAproveitamentoModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleFinalConfirm}
        examName={currentExamName}
      />
    </>
  );
};

export default ExamesAnterioresModal;
